import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { LayoutDashboard, FileText, Newspaper, Image as ImageIcon, Inbox, Settings as SettingsIcon, LogOut, Users, Menu, X } from "lucide-react";

const items = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/pages", icon: FileText, label: "Pages", testid: "admin-nav-pages" },
  { to: "/admin/blog", icon: Newspaper, label: "Blog", testid: "admin-nav-blog" },
  { to: "/admin/media", icon: ImageIcon, label: "Media library", testid: "admin-nav-media" },
  { to: "/admin/inbox", icon: Inbox, label: "Contact inbox", testid: "admin-nav-inbox" },
  { to: "/admin/admins", icon: Users, label: "Administrators", testid: "admin-nav-admins" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings", testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login", { replace: true });
    else if (!user.is_admin) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  // Close drawer when route changes (mobile UX)
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-neutral-500" data-testid="admin-loading">Loading…</div>;
  }

  const sidebar = (
    <>
      <Link to="/" className="px-6 py-6 border-b border-neutral-200 block">
        <p className="font-serif-display text-xl text-[#1a1a1a]">Spirit <span className="text-[#6B1F2A]">&</span> Associates</p>
        <p className="text-xs text-neutral-500 mt-1">Admin Console</p>
      </Link>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end}
            className={({ isActive }) => `admin-sidenav-item ${isActive ? "active" : ""}`}
            data-testid={it.testid}>
            <it.icon size={16} />
            <span>{it.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-neutral-200">
        <div className="px-3 py-2 mb-2">
          <p className="text-xs text-neutral-500">Signed in as</p>
          <p className="text-sm font-medium truncate">{user.email}</p>
        </div>
        <button onClick={logout} className="admin-sidenav-item w-full text-left" data-testid="admin-logout">
          <LogOut size={16} />
          <span>Sign out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#F7F7F7]" data-testid="admin-layout">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-neutral-200 flex items-center gap-3 px-4 h-14">
        <button onClick={() => setDrawerOpen(true)} className="p-2 -ml-2 text-neutral-700 hover:bg-neutral-100 rounded" aria-label="Open menu" data-testid="admin-drawer-open">
          <Menu size={20} />
        </button>
        <Link to="/admin" className="flex-1 min-w-0">
          <p className="font-serif-display text-lg text-[#1a1a1a] truncate">Spirit <span className="text-[#6B1F2A]">&</span> Associates</p>
        </Link>
        <span className="text-[10px] uppercase tracking-wider text-neutral-500">Admin</span>
      </header>

      {/* Desktop sidebar (always visible at lg+) */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-neutral-200 flex-col z-20">
        {sidebar}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw] bg-white flex flex-col shadow-xl">
            <button onClick={() => setDrawerOpen(false)} className="absolute top-3 right-3 p-2 text-neutral-500 hover:bg-neutral-100 rounded" aria-label="Close menu" data-testid="admin-drawer-close">
              <X size={20} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <main className="lg:ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
